'use client';

import { useState, useEffect } from 'react';
import { Globe, Plus } from 'lucide-react';
import { useDeveloper } from '@/hooks/useDeveloper';
import { Card, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { Skeleton } from '@/components/ui/skeleton';

export function WebhooksPage() {
  const { webhooks, loading, fetchWebhooks, updateWebhook } = useDeveloper();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<any>(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const handleEdit = (webhook: any) => {
    setEditingWebhook(webhook);
    setWebhookUrl(webhook?.url || '');
    setWebhookSecret(webhook?.secret || '');
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingWebhook) {
      // Create new webhook
      const response = await updateWebhook({ url: webhookUrl, secret: webhookSecret });
      if (response) {
        toast.success('Webhook created');
      }
    } else {
      // Update existing
      const result = await updateWebhook({ url: webhookUrl, secret: webhookSecret });
      if (result) {
        toast.success('Webhook updated');
      }
    }
    setEditDialogOpen(false);
  };

  if (loading && webhooks.length === 0) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card className="glass">
          <CardContent className="pt-6">
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Webhooks</h1>
          <p className="text-slate-400 mt-1">Manage your webhook endpoints</p>
        </div>
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              setEditingWebhook(null);
              setWebhookUrl('');
              setWebhookSecret('');
              setEditDialogOpen(true);
            }}>
              <Plus className="mr-2 h-4 w-4" />
              Add Webhook
            </Button>
          </DialogTrigger>
          <DialogContent className="glass max-w-md">
            <DialogHeader>
              <DialogTitle>{editingWebhook ? 'Edit Webhook' : 'Add Webhook'}</DialogTitle>
              <CardDescription>
                Configure your webhook endpoint to receive real-time events
              </CardDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="webhook-url">Webhook URL</Label>
                <Input
                  id="webhook-url"
                  placeholder="https://your-server.com/webhook"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="webhook-secret">Secret (for HMAC verification)</Label>
                <Input
                  id="webhook-secret"
                  type="password"
                  placeholder="Enter a secure secret"
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {webhooks.length === 0 ? (
        <Card className="glass">
          <CardContent className="pt-6 text-center py-12">
            <Globe className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-4">No webhooks configured</p>
            <Button onClick={() => setEditDialogOpen(true)}>
              Add Your First Webhook
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {webhooks.map((webhook) => (
            <Card key={webhook.id} className="glass">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Globe className="h-4 w-4 text-cyan-400" />
                      <p className="font-medium text-white">{webhook.url}</p>
                      <Badge variant={webhook.active ? 'default' : 'secondary'}>
                        {webhook.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-400 mb-2">
                      Failed deliveries: {webhook.failureCount || 0}
                    </p>
                    {webhook.lastTriggeredAt && (
                      <p className="text-xs text-slate-500">
                        Last triggered: {new Date(webhook.lastTriggeredAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={webhook.active}
                      onCheckedChange={async () => {
                        await updateWebhook({ url: webhook.url, secret: webhook.secret });
                        toast.success('Webhook updated');
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(webhook)}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
